import React from 'react';

/**
 * ErrorBoundary — wraps all page renders in App.jsx
 * When any React component throws (runtime crash, bad data, emoji encoding etc.)
 * this shows a recovery UI instead of a blank screen.
 * Industry standard — every production React app must have this.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error('[MarketPulze] Runtime error caught by ErrorBoundary:', error, errorInfo);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const msg = this.state.error?.message || 'Unknown error';
    const stack = this.state.errorInfo?.componentStack || '';

    return (
      <div style={{
        minHeight: '100vh', background: '#0f1923',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '2rem', fontFamily: 'system-ui, sans-serif',
      }}>
        <div style={{
          maxWidth: 600, width: '100%',
          background: '#1a2535', border: '1px solid rgba(239,68,68,0.4)',
          borderTop: '4px solid #ef4444',
          borderRadius: 12, padding: '2rem',
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>
            Something went wrong
          </div>
          <div style={{ color: '#ef4444', fontWeight: 700, fontSize: '1rem', marginBottom: '1rem' }}>
            {msg}
          </div>
          <div style={{ color: '#64748b', fontSize: '0.8rem', marginBottom: '1.5rem' }}>
            The application encountered an unexpected error. This has been logged.
            Your data is safe — this is a display error only.
          </div>
          {process.env.NODE_ENV !== 'production' && stack && (
            <pre style={{
              background: '#0f1923', border: '1px solid #2d4060',
              borderRadius: 8, padding: '1rem',
              fontSize: '0.7rem', color: '#94a3b8',
              overflowX: 'auto', marginBottom: '1.5rem',
              maxHeight: 200, overflowY: 'auto',
            }}>
              {stack.trim()}
            </pre>
          )}
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '0.65rem 1.5rem',
                background: 'linear-gradient(135deg,#00ff88,#00d4ff)',
                border: 'none', borderRadius: 8,
                color: '#050d1a', fontWeight: 800,
                cursor: 'pointer', fontSize: '0.9rem', fontFamily: 'inherit',
              }}>
              Reload Page
            </button>
            <button
              onClick={() => { window.location.href = '/'; }}
              style={{
                padding: '0.65rem 1.5rem',
                background: 'transparent',
                border: '1px solid rgba(0,212,255,0.3)',
                borderRadius: 8, color: '#00d4ff',
                fontWeight: 700, cursor: 'pointer',
                fontSize: '0.9rem', fontFamily: 'inherit',
              }}>
              Go to Home
            </button>
          </div>
        </div>
        <div style={{ color: '#475569', fontSize: '0.7rem', marginTop: '1rem' }}>
          MarketPulze — Error ID: {Date.now().toString(36).toUpperCase()}
        </div>
      </div>
    );
  }
}
